/**
 * The site bundle (public/js/index.js), loaded at the end of <body> on every
 * page by layout.antlers.html.
 *
 * Each feature lives in ./features and exports one `init…` function that
 * finds its own markup and does nothing when the page has none. This file only
 * decides the order and the phase in which they run, through `runFeature`,
 * which reports a throwing feature to Sentry and moves on instead of aborting
 * everything after it (see lib/runFeature.js — why this bundle is split).
 *
 * Two phases, the same two the previous single-file version had:
 *  1. at script execution — the DOM is parsed by then, and the MyHU wiring
 *     has to register its listener before auth.js's first microtask;
 *  2. on jQuery `ready` — everything that measures or decorates the page.
 */
import $ from 'jquery';
import Toastify from 'toastify-js';
import 'bootstrap';                 // Bootstrap's data-api: collapse, dropdown, carousel, …
import 'slick-carousel';            // $.fn.slick — features/sliders
// moment + daterangepicker (features/contactGroupsForm) and the <lottie-player>
// element (features/lottiePlayer) are NOT imported here: they were 433 KB of the
// bundle for markup that only a handful of pages have, so each feature pulls its
// own chunk when it finds that markup. See webpack.mix.js for where chunks land.

import { runFeature } from './lib/runFeature';
import { initMyHuLogin } from './features/myhuLogin';
import { initSmoothScroll } from './features/smoothScroll';
import { initFooterBadgeRotation } from './features/footerBadgeRotation';
import { initCarouselCounter } from './features/carouselCounter';
import { initLanguageBanner } from './features/languageBanner';
import { initNationalityField } from './features/nationalityField';
import { initSliders } from './features/sliders';
import { initContactGroupsForm } from './features/contactGroupsForm';
import { initLottiePlayer } from './features/lottiePlayer';
import { initAnimatedHeadline } from './features/animatedHeadline';
import { initPropertyFilter } from './features/propertyFilter';
import { initMarqueePanels } from './features/marqueePanels';
import { initScrollAnimations } from './features/scrollAnimations';
import { initHoverLines } from './features/hoverLines';
import { initDesktopMenuHover } from './features/desktopMenuHover';
import { initNewsletterForm } from './features/newsletterForm';
import { initEmailSuggest } from './features/emailSuggest';
import { initMobileMenuBolOverlay } from './features/mobileMenuBolOverlay';
import { initHuItalics } from './features/huItalics';

// `$` here IS the page's jQuery, the one the layout loads in <head> — webpack
// resolves the import to the global instead of bundling a copy, see the
// `externals` note in webpack.mix.js. So this file no longer assigns
// `window.jQuery`: everything the bundle registers (slick, daterangepicker,
// $.fn.hpanel) now lands on the same object as fancybox and as the inline
// scripts of the templates.
//
// `Toastify` still has to be published by hand: partial/_formlanding.antlers.html
// calls it seven times for the send/validation feedback of the Human Travel
// landing form and it was undefined, because importing toastify-js as an ES
// module here never put it on `window`.
window.Toastify = Toastify;

// Phase 1 — while the bundle executes.
runFeature('myhu-login', initMyHuLogin);
runFeature('smooth-scroll', initSmoothScroll);
runFeature('footer-badge-rotation', initFooterBadgeRotation);
runFeature('carousel-counter', initCarouselCounter);

// Phase 2 — DOM ready.
$(function () {
    runFeature('language-banner', initLanguageBanner);
    runFeature('nationality-field', initNationalityField);
    runFeature('sliders', initSliders);
    runFeature('contact-groups-form', initContactGroupsForm);
    runFeature('lottie-player', initLottiePlayer);
    runFeature('animated-headline', initAnimatedHeadline);
    runFeature('property-filter', initPropertyFilter);
    runFeature('marquee-panels', initMarqueePanels);
    runFeature('scroll-animations', initScrollAnimations);
    runFeature('hover-lines', initHoverLines);
    runFeature('desktop-menu-hover', initDesktopMenuHover);
    runFeature('newsletter-form', initNewsletterForm);
    runFeature('email-suggest', initEmailSuggest);
    runFeature('mobile-menu-bol-overlay', initMobileMenuBolOverlay);
    // Last: it decorates text the features above may have added (slick's cloned slides).
    runFeature('hu-italics', initHuItalics);
});
